import { useMemo, useState } from "react";
import {
  Clock,
  ListOrdered,
  Loader2,
  Route,
  Settings,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierTripDetail,
  useComputeCarrierTripRoute,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { FocusTripMap } from "./focus-trip-map";
import { buildStops } from "./trip-stops";
import type { TaskFailureTarget } from "./task-failure";
import { TripCompletionAction } from "./trip-completion-action";
import {
  RouteSettingsDialog,
  RouteStartTimeDialog,
} from "./trip-route-dialogs";
import {
  responseError,
  TripWorkflowFeedback,
  type WorkflowFeedbackValue,
} from "./trip-routing-feedback";
import { isTripRoutingEditable } from "./trip-routing-state";
import { TripStopsWorkflow } from "./trip-stops-workflow";

/** Coordinates the focused route, stop, waypoint, and lifecycle workflows. */
export function TripRoutingWorkspace({
  detail,
  onFailTask,
}: {
  detail: CarrierTripDetail;
  onFailTask?: (task: TaskFailureTarget) => void;
}) {
  return (
    <TripRoutingWorkspaceContent
      key={detail.id}
      detail={detail}
      onFailTask={onFailTask}
    />
  );
}

function ToolButton({
  icon,
  label,
  primary,
  busy,
  disabled,
  pressed,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  primary?: boolean;
  busy?: boolean;
  disabled?: boolean;
  pressed?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant={primary ? "default" : "outline"}
      size="sm"
      aria-pressed={pressed}
      disabled={disabled || busy}
      className="h-8 gap-1.5 text-[12px]"
      onClick={onClick}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
      {label}
    </Button>
  );
}

/** Distance and estimated duration, shown only once the Server knows them. */
function RouteFacts({ detail }: { detail: CarrierTripDetail }) {
  const hours =
    detail.estimatedDuration != null
      ? Math.floor(detail.estimatedDuration / 60)
      : null;

  return (
    <div className="flex items-center gap-3 text-[11.5px] text-zinc-600">
      {detail.distance != null && (
        <span className="flex items-center gap-1">
          <Route className="h-3.5 w-3.5 text-zinc-400" />
          <b className="tabular-nums">{detail.distance.toFixed(1)}</b> km
        </span>
      )}
      {detail.estimatedDuration != null && hours != null && (
        <span className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5 text-zinc-400" />
          <b className="tabular-nums">
            {hours}h {detail.estimatedDuration % 60}m
          </b>
        </span>
      )}
    </div>
  );
}

function TripRoutingWorkspaceContent({
  detail,
  onFailTask,
}: {
  detail: CarrierTripDetail;
  onFailTask?: (task: TaskFailureTarget) => void;
}) {
  const [feedback, setFeedback] = useState<WorkflowFeedbackValue | null>(null);
  const [armed, setArmed] = useState(false);
  const [selectedWaypointId, setSelectedWaypointId] = useState<string | null>(
    null
  );
  const stops = useMemo(() => buildStops(detail.waypoints), [detail.waypoints]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [startTimeOpen, setStartTimeOpen] = useState(false);
  const computeRoute = useComputeCarrierTripRoute();
  const editable = isTripRoutingEditable(detail.status);

  const reportSuccess = (message: string) => {
    setFeedback({ tone: "success", message });
    toast.success(message);
  };
  const reportError = (error: unknown, fallback: string) => {
    setFeedback({ tone: "error", message: responseError(error, fallback) });
  };

  const optimize = async () => {
    try {
      await computeRoute.mutateAsync({ tripId: detail.id });
      reportSuccess("Route and ETAs updated.");
    } catch (error) {
      reportError(error, "Route could not be computed. Check stop locations.");
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5">
        <ToolButton
          icon={<Zap className="h-3.5 w-3.5" />}
          label="Optimize route"
          primary
          busy={computeRoute.isPending}
          disabled={!editable || detail.waypoints.length === 0}
          onClick={optimize}
        />
        <ToolButton
          icon={<ListOrdered className="h-3.5 w-3.5" />}
          label="Reorder"
          primary={armed}
          pressed={armed}
          disabled={!editable || detail.waypoints.length < 2}
          onClick={() => setArmed((value) => !value)}
        />
        <ToolButton
          icon={<Settings className="h-3.5 w-3.5" />}
          label="Route settings"
          onClick={() => setSettingsOpen(true)}
        />
        <ToolButton
          icon={<Clock className="h-3.5 w-3.5" />}
          label="Start time"
          onClick={() => setStartTimeOpen(true)}
        />
        <TripCompletionAction
          detail={detail}
          reportSuccess={reportSuccess}
          reportError={reportError}
        />
        <div className="flex-1" />
        <RouteFacts detail={detail} />
      </div>

      {feedback && (
        <div className="border-b border-zinc-100 px-4 py-2">
          <TripWorkflowFeedback value={feedback} />
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-rows-[minmax(220px,1fr)_minmax(0,1.2fr)] lg:grid-cols-[1.15fr_1fr] lg:grid-rows-1">
        <div className="min-h-0 border-b border-zinc-200 lg:border-b-0 lg:border-r">
          <FocusTripMap
            detail={detail}
            stops={stops}
            selectedId={selectedWaypointId}
            onSelect={(stop) =>
              setSelectedWaypointId((current) =>
                current === stop.id ? null : stop.id
              )
            }
          />
        </div>
        <TripStopsWorkflow
          detail={detail}
          armed={armed}
          selectedWaypointId={selectedWaypointId}
          onSelectWaypoint={setSelectedWaypointId}
          onFailTask={onFailTask}
          reportSuccess={reportSuccess}
          reportError={reportError}
        />
      </div>

      <RouteSettingsDialog
        detail={detail}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        reportSuccess={reportSuccess}
      />
      <RouteStartTimeDialog
        detail={detail}
        open={startTimeOpen}
        onOpenChange={setStartTimeOpen}
        reportSuccess={reportSuccess}
      />
    </div>
  );
}
